import type { Font, TemplateAsset } from "@/api/client";
import {
  clampPosition,
  innerBox,
  snapMM,
  textHeightMM,
  type Field,
  type Page,
} from "@/features/templates/designer/geometry";

/** Binds a text or QR field accepts (internal/render/validate.go textBinds). */
export const TEXT_BINDS = [
  "member.full_name",
  "member.email",
  "member.membership_no",
  "certificate.membership_no",
  "certificate.doc_control_id",
  "certificate.verify_token",
  "certificate.issued_at",
  "program.name",
  "program.type",
  "certificate.verify_url",
  "certificate.verification_code",
] as const;

/** A QR field offers the verification link first. */
export const QR_BINDS = ["certificate.verify_url", ...TEXT_BINDS.filter((b) => b !== "certificate.verify_url")] as const;

export const BIND_LABELS: Partial<Record<string, string>> = {
  "certificate.verify_url": "Verification link (QR)",
  "certificate.verification_code": "Verification code",
};
export const bindLabel = (bind: string) => BIND_LABELS[bind] ?? bind;

export const FONT_STYLES = ["regular", "bold", "italic", "bolditalic"] as const;
export const STYLE_LABELS: Record<(typeof FONT_STYLES)[number], string> = {
  regular: "Regular",
  bold: "Bold",
  italic: "Italic",
  bolditalic: "Bold italic",
};

export type NewField =
  | { kind: "text"; bind: string }
  | { kind: "qr" }
  | { kind: "photo" }
  | { kind: "signature" }
  | { kind: "image"; asset: TemplateAsset };

const NEW_TEXT_PT = 14;
const NEW_IMAGE_W = 40;

/** A new field centred inside the margins, at a size the validator accepts. */
export function newField(spec: NewField, page: Page): Field {
  const inner = innerBox(page);
  const centred = (field: Field, w: number, h: number): Field => ({
    ...field,
    ...clampPosition(field, inner.x + inner.w / 2 - w / 2, inner.y + inner.h / 2 - h / 2, page),
  });
  switch (spec.kind) {
    case "text": {
      // Spanning the margins with centred text reads well for most binds; x stays on the left margin.
      const field: Field = { type: "text", bind: spec.bind, x_mm: 0, y_mm: 0, w_mm: inner.w, size_pt: NEW_TEXT_PT, align: "center" };
      return { ...field, x_mm: inner.x, y_mm: snapMM(inner.y + inner.h / 2 - textHeightMM(NEW_TEXT_PT) / 2) };
    }
    case "qr":
      return centred({ type: "qr", bind: "certificate.verify_url", x_mm: 0, y_mm: 0, size_mm: 25 }, 25, 25);
    case "photo":
      return centred({ type: "image", bind: "member.photo", x_mm: 0, y_mm: 0, w_mm: 30, h_mm: 38 }, 30, 38);
    case "signature":
      return centred({ type: "signature", bind: "signature.image", x_mm: 0, y_mm: 0, w_mm: 40, h_mm: 15 }, 40, 15);
    case "image": {
      // Keep the image's aspect ratio, at most 40 mm wide and no taller than the area inside the margins.
      const ratio = spec.asset.height_px / spec.asset.width_px;
      const w = Math.min(NEW_IMAGE_W, inner.h / ratio);
      const h = w * ratio;
      return centred({ type: "image", asset_key: spec.asset.id, x_mm: 0, y_mm: 0, w_mm: w, h_mm: h }, w, h);
    }
  }
}

/** The canvas and inspector name for a field, e.g. "Text field member.full_name". */
export function fieldLabel(field: Field, assets: TemplateAsset[]): string {
  const bind = field.bind || "(no bind)";
  switch (field.type) {
    case "text":
      return `Text field ${bind}`;
    case "qr":
      return `QR field ${bind}`;
    case "signature":
      return `Signature field ${bind}`;
    default:
      if (!field.asset_key) return `Photo field ${bind}`;
      return `Image field ${assets.find((a) => a.id === field.asset_key)?.name ?? field.asset_key}`;
  }
}

/** The uploaded file a text field draws with, or undefined for the built-in Helvetica or a missing upload. */
export const fontFor = (field: Field, fonts: Font[]) =>
  field.font ? fonts.find((f) => f.family === field.font && f.style === (field.font_style ?? "regular")) : undefined;

/** One CSS family per uploaded file, as the font library registers it. */
export const fontFamilyName = (font: Font) => `cipm-font-${font.id}`;

/** The renderer accepts "C" and "center" alike; the canvas and inspector show one of three. */
export function alignOf(field: Field): "left" | "center" | "right" {
  switch (String(field.align ?? "").trim().toLowerCase()) {
    case "c":
    case "center":
      return "center";
    case "r":
    case "right":
      return "right";
    default:
      return "left";
  }
}
